import { IonButton, IonLabel, IonRow } from '@ionic/react';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { GenericMessages } from '../../intl/GenericMessages';
import { JobMessages } from '../../intl/JobMessages';
import type { Permission } from '../../models/bundle';
import type { JobLogPermission } from '../../models/jobLog';
import { AppModal } from '../../components/reusable-components/app-modal/AppModal';
import { DqText } from '../../components/dq-text';

interface UnshareModalProps {
  permissions: Permission[] | JobLogPermission[] | undefined;
  onSubmit: (removed: string[]) => Promise<void>;
  onClose: () => Promise<void>;
  isOpen: boolean;
  title: string;
  description: string;
}

export interface UnshareModalRowProps {
  isShared: boolean;
  perm: Permission | JobLogPermission;
  toggle: (principal: string) => void;
  disabled: boolean;
  showButton?: boolean;
}

export const UnshareModalRow: React.FC<UnshareModalRowProps> = props => {
  const { formatMessage } = useIntl();
  const { isShared, perm, toggle } = props;

  const hideButton = props.showButton ?? true;
  return (
    <IonRow className="ion-align-items-center">
      <DqText style={{ flex: 1 }}>{perm.principalName}</DqText>
      {hideButton && (
        <IonButton
          fill="clear"
          color={isShared ? 'danger' : 'success'}
          onClick={() => toggle(perm.principal)}
          disabled={props.disabled}
        >
          {formatMessage(
            isShared ? JobMessages.unshareJobSelectForUnsharing : JobMessages.unshareJobDeselectForUnsharing,
          )}
        </IonButton>
      )}
    </IonRow>
  );
};

const defaultPermissions: Permission[] | JobLogPermission[] = [];

export const UnshareModal: FC<UnshareModalProps> = props => {
  const { onClose, onSubmit } = props;
  const { formatMessage } = useIntl();
  const [unshared, setUnshared] = useState<string[]>([]);
  const [isLoading, setLoading] = useState(false);
  const permissions = props.permissions ?? defaultPermissions;

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      await onSubmit(unshared);
      setUnshared([]); // clear the unshared list for next time its opened
    } finally {
      setLoading(false);
    }
  }, [onSubmit, unshared]);

  const handleCancel = useCallback(async () => {
    try {
      setLoading(true);
      await onClose();
      setUnshared([]); // clear the unshared list for next time its opened
    } finally {
      setLoading(false);
    }
  }, [onClose]);

  const toggle = useCallback((orgId: string) => {
    setUnshared(prev => {
      if (prev.includes(orgId)) {
        return prev.filter(id => id !== orgId);
      } else {
        return [...prev, orgId];
      }
    });
  }, []);

  const submitButton = useMemo(() => {
    return {
      label: formatMessage(JobMessages.unshareJobConfirmationButtonText),
      onClick: handleSubmit,
      disabled: unshared.length === 0 || isLoading,
    };
  }, [formatMessage, handleSubmit, isLoading, unshared.length]);

  const cancelButton = useMemo(() => {
    return {
      label: formatMessage(GenericMessages.cancelButtonLabel),
      onClick: handleCancel,
      disabled: isLoading,
    };
  }, [formatMessage, handleCancel, isLoading]);

  return (
    <AppModal
      isOpen={props.isOpen}
      title={props.title}
      onClose={props.onClose}
      primaryButton={submitButton}
      secondaryButton={cancelButton}
    >
      <IonRow className="ion-padding-vertical">
        <IonLabel>{props.description}</IonLabel>
      </IonRow>
      {permissions.map((p, i) => {
        return (
          <UnshareModalRow
            key={`${p.principal}${i}`}
            perm={p}
            isShared={!unshared.includes(p.principal)}
            toggle={toggle}
            disabled={isLoading}
          />
        );
      })}
    </AppModal>
  );
};
